// Shared constants, types and validators for the CRM service catalog (Fase 2).
// Kept free of Convex server imports so it can be reused by the frontend for
// labels and by the backend for validation.

import { v } from "convex/values";

// The five supported locales (must match src/i18n-locales.ts).
export const SERVICE_LOCALES = ["it", "en", "es", "fr", "de"] as const;
export type ServiceLocale = (typeof SERVICE_LOCALES)[number];

// `classic` is retained as the stable persisted/API identifier for existing
// orders. Publicly it is presented as the extensible "Standard" tier.
export const SERVICE_OFFER_VARIANTS = ["classic", "ai", "pro"] as const;
export type ServiceOfferVariant = (typeof SERVICE_OFFER_VARIANTS)[number];

export const SERVICE_OFFER_COPY: Record<
  ServiceLocale,
  Record<ServiceOfferVariant, { title: string; description: string }>
> = {
  it: {
    classic: {
      title: "Versione Standard",
      description:
        "Metodo umano, progettazione su misura e lavorazione diretta del team Enkey.",
    },
    ai: {
      title: "Versione AI",
      description:
        "Uso intensivo dell’intelligenza artificiale, con direzione, controllo e verifica finale del team Enkey.",
    },
    pro: {
      title: "Versione Pro",
      description:
        "Competenze umane e intelligenza artificiale avanzata integrate, con direzione strategica e revisione approfondita del team Enkey.",
    },
  },
  en: {
    classic: {
      title: "Standard version",
      description:
        "Traditional method, tailored design and hands-on delivery by the Enkey team.",
    },
    ai: {
      title: "AI version",
      description:
        "Intensive use of artificial intelligence, directed, reviewed and finally approved by the Enkey team.",
    },
    pro: {
      title: "Pro version",
      description:
        "Human expertise and advanced artificial intelligence combined, with strategic direction and in-depth review by the Enkey team.",
    },
  },
  es: {
    classic: {
      title: "Versión Estándar",
      description:
        "Método tradicional, diseño a medida y realización directa por el equipo de Enkey.",
    },
    ai: {
      title: "Versión con IA",
      description:
        "Uso intensivo de inteligencia artificial, con dirección, control y revisión final del equipo de Enkey.",
    },
    pro: {
      title: "Versión Pro",
      description:
        "Experiencia humana e inteligencia artificial avanzada integradas, con dirección estratégica y revisión exhaustiva del equipo de Enkey.",
    },
  },
  fr: {
    classic: {
      title: "Version Standard",
      description:
        "Méthode traditionnelle, conception sur mesure et réalisation directe par l’équipe Enkey.",
    },
    ai: {
      title: "Version avec IA",
      description:
        "Usage intensif de l’intelligence artificielle, avec direction, contrôle et validation finale par l’équipe Enkey.",
    },
    pro: {
      title: "Version Pro",
      description:
        "Expertise humaine et intelligence artificielle avancée réunies, avec direction stratégique et révision approfondie par l’équipe Enkey.",
    },
  },
  de: {
    classic: {
      title: "Standard-Version",
      description:
        "Traditionelle Arbeitsweise, individuelle Planung und direkte Umsetzung durch das Enkey-Team.",
    },
    ai: {
      title: "Version mit KI",
      description:
        "Intensiver Einsatz künstlicher Intelligenz mit Steuerung, Kontrolle und abschließender Prüfung durch das Enkey-Team.",
    },
    pro: {
      title: "Pro-Version",
      description:
        "Menschliche Expertise und fortschrittliche KI kombiniert, mit strategischer Steuerung und gründlicher Prüfung durch das Enkey-Team.",
    },
  },
};

// Billing modes for a service or variant.
export const BILLING_MODES = [
  "one_time", // una tantum
  "subscription", // solo abbonamento
  "hybrid", // una tantum + abbonamento
  "quote", // prezzo su preventivo
] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export function isBillingMode(value: string): value is BillingMode {
  return (BILLING_MODES as readonly string[]).includes(value);
}

// Recurring intervals for subscription/hybrid modes.
export const RECURRING_INTERVALS = [
  "monthly",
  "quarterly",
  "biannual",
  "annual",
  "custom",
] as const;
export type RecurringInterval = (typeof RECURRING_INTERVALS)[number];

export function isRecurringInterval(value: string): value is RecurringInterval {
  return (RECURRING_INTERVALS as readonly string[]).includes(value);
}

// Service lifecycle statuses.
export const SERVICE_STATUSES = ["draft", "published", "archived"] as const;
export type ServiceStatus = (typeof SERVICE_STATUSES)[number];

export function isServiceStatus(value: string): value is ServiceStatus {
  return (SERVICE_STATUSES as readonly string[]).includes(value);
}

const LEGACY_INTERNAL_CRM_ORIGIN = "https://lab.enkey.eu/crm";

export function normalizeReservedBookingUrl(
  value: string | undefined,
): string | undefined {
  if (
    value === LEGACY_INTERNAL_CRM_ORIGIN ||
    value?.startsWith(`${LEGACY_INTERNAL_CRM_ORIGIN}/`)
  ) {
    return `https://www.enkey.eu${value.slice("https://lab.enkey.eu".length)}`;
  }
  return value;
}

// Category lifecycle statuses.
export const CATEGORY_STATUSES = ["active", "archived"] as const;
export type CategoryStatus = (typeof CATEGORY_STATUSES)[number];

// Revision change types (for the storico revisioni timeline).
export const REVISION_CHANGE_TYPES = [
  "created",
  "updated",
  "published",
  "archived",
  "restored",
] as const;
export type RevisionChangeType = (typeof REVISION_CHANGE_TYPES)[number];

// ----- Convex validators (mirror the schema helpers) ------------------------

export const localizedTextValidator = v.object({
  it: v.string(),
  en: v.string(),
  es: v.string(),
  fr: v.string(),
  de: v.string(),
});

export const serviceContentFieldsValidator = v.object({
  title: v.string(),
  tagline: v.string(),
  summary: v.string(),
  description: v.string(),
  description2: v.string(),
  features: v.array(v.string()),
  limitations: v.array(v.string()),
  ctaLabel: v.string(),
  priceText: v.string(),
  seoTitle: v.string(),
  metaDescription: v.string(),
});

export const localizedServiceContentValidator = v.object({
  it: serviceContentFieldsValidator,
  en: serviceContentFieldsValidator,
  es: serviceContentFieldsValidator,
  fr: serviceContentFieldsValidator,
  de: serviceContentFieldsValidator,
});

// The TypeScript shape of a single locale's editorial content.
export type ServiceContentFields = {
  title: string;
  tagline: string;
  summary: string;
  description: string;
  description2: string;
  features: string[];
  limitations: string[];
  ctaLabel: string;
  priceText: string;
  seoTitle: string;
  metaDescription: string;
};

export type LocalizedText = Record<ServiceLocale, string>;
export type LocalizedServiceContent = Record<
  ServiceLocale,
  ServiceContentFields
>;

// An empty content block, used when creating a new blank locale.
export function emptyContentFields(): ServiceContentFields {
  return {
    title: "",
    tagline: "",
    summary: "",
    description: "",
    description2: "",
    features: [],
    limitations: [],
    ctaLabel: "",
    priceText: "",
    seoTitle: "",
    metaDescription: "",
  };
}

export function emptyLocalizedContent(): LocalizedServiceContent {
  return {
    it: emptyContentFields(),
    en: emptyContentFields(),
    es: emptyContentFields(),
    fr: emptyContentFields(),
    de: emptyContentFields(),
  };
}

// A locale is considered "complete" when the essential public-facing fields are
// filled: title, tagline, summary and the first description paragraph. This
// drives the per-language completeness badge in the CRM.
export function isLocaleComplete(content: ServiceContentFields): boolean {
  return (
    content.title.trim().length > 0 &&
    content.tagline.trim().length > 0 &&
    content.summary.trim().length > 0 &&
    content.description.trim().length > 0
  );
}

// ----- Public site shapes ---------------------------------------------------
// Resolved shape served to the public website (Services page + detail pages).
// It flattens a single locale's editorial content (with IT fallback) together
// with the presentation + pricing so the SPA and prerender render identically
// whether the data comes from the CRM catalog or the static fallback.

export type PublicServicePricing = {
  billingMode: BillingMode;
  currency: string;
  oneTimeAmount?: number;
  recurringAmount?: number;
  recurringInterval?: string;
  recurringIntervalCount?: number;
  vatIncluded: boolean;
  priceFrom: boolean;
  publicPrice: boolean;
  estimatedDaysMin?: number;
  estimatedDaysMax?: number;
  timeReductionPercent?: number;
  promotion?: {
    name: string;
    kind?: "automatic" | "voucher";
    voucherRequired?: boolean;
    /** Largest active component discount, retained for old consumers. */
    discountPercent: number;
    oneTimeDiscountPercent?: number;
    recurringDiscountPercent?: number;
    startsOn?: string;
    endsOn?: string;
    originalOneTimeAmount?: number;
    originalRecurringAmount?: number;
    /** Optional seasonal subtitle, e.g. Cyber Days · Start. */
    seasonalBadge?: string;
  };
};

export type PublicServiceOffer = PublicServicePricing & {
  variant: ServiceOfferVariant;
  title: string;
  description: string;
  enabled: boolean;
};

export function discountedServiceAmount(
  amount: number | undefined,
  discountPercent: number,
): number | undefined {
  if (amount === undefined) return undefined;
  return Math.round(amount * (100 - discountPercent)) / 100;
}

export function servicePromotionDiscountLabel(
  promotion: NonNullable<PublicServicePricing["promotion"]>,
  labels: { oneTime: string; recurring: string } = {
    oneTime: "setup",
    recurring: "monthly",
  },
): string {
  const setup = promotion.oneTimeDiscountPercent;
  const recurring = promotion.recurringDiscountPercent;
  if (setup !== undefined && recurring !== undefined && setup !== recurring) {
    return `-${setup}% ${labels.oneTime} · -${recurring}% ${labels.recurring}`;
  }
  return `-${setup ?? recurring ?? promotion.discountPercent}%`;
}

export type PublicService = {
  serviceKey: string;
  /** Canonical localized slug for the requested locale. */
  slug: string;
  /** All localized slugs, used for hreflang alternates. */
  slugs: LocalizedText;
  icon: string;
  gradient: string;
  bookingUrl?: string;
  imageUrl?: string;
  title: string;
  tagline: string;
  summary: string;
  description: string;
  description2: string;
  features: string[];
  ctaLabel: string;
  priceText: string;
  seoTitle: string;
  metaDescription: string;
  /** True when one or more fields fell back to Italian for this locale. */
  usedFallback: boolean;
  order: number;
  /** Dedicated commercial options. Standard pricing remains at root. */
  aiOffer: PublicServiceOffer;
  proOffer: PublicServiceOffer;
} & PublicServicePricing;
